import React from "react";
import { X } from "lucide-react";

type TermsAndConditionProps = {
  onClose: () => void;
  onAccept?: () => void;
};

const TermsAndCondition = ({ onClose, onAccept }: TermsAndConditionProps) => {
  const handleAccept = () => {
    onAccept?.();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(18,23,43,0.55)] p-[1.5rem]"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-[52rem] max-h-[88vh] flex-col overflow-hidden rounded-[1rem] bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="eula-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-[1rem] border-b border-[#EDEFF3] px-[1.5rem] pb-[1rem] pt-[1.5rem]">
          <div className="min-w-0">
            <h1
              id="eula-title"
              className="mb-[0.25rem] text-[1.25rem] font-bold leading-[1.75rem] tracking-[-0.15px] text-[#01187D]"
            >
              End User License Agreement
            </h1>
            <div className="font-mono text-[0.75rem] leading-[1rem] tabular-nums text-[#6C727F]">
              Last updated 13 Jun 2023
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="inline-flex h-[2rem] w-[2rem] min-w-[2rem] items-center justify-center rounded-[0.375rem] border-none bg-transparent text-[#6C727F] transition-colors duration-[180ms] hover:bg-[#EBECEC] hover:text-[#12172B] focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_2px_#01187D] active:translate-y-px active:opacity-90"
          >
            <X className="h-[1.125rem] w-[1.125rem]" strokeWidth={1.75} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-[1.5rem] py-[1.25rem]">
          <div className="rounded-[0.5rem] border border-[#DDDFE4] bg-[#F9FAFB] p-[1.25rem]">
            <section className="mb-[1.25rem]">
              <p className="mb-[0.625rem] text-[0.875rem] leading-[1.25rem] text-[#6C727F]">
                Please read this End User License Agreement (&quot;Agreement&quot;)
                carefully before downloading, installing, or using OneCap
                (&quot;Software&quot; or &quot;Application&quot;) provided by OneCap Technologies
                Pvt. Ltd. (&quot;Company&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;).
              </p>
              <p className="mb-0 text-[0.875rem] leading-[1.25rem] text-[#6C727F]">
                By installing or using the Software, you agree to be bound by
                the terms of this Agreement. If you do not agree, do not install
                or use the Software.
              </p>
            </section>

            <section className="mb-[1.25rem]">
              <h3 className="mb-[0.5rem] text-[1rem] font-bold leading-[1.5rem] text-[#12172B]">
                1. License Grant
              </h3>
              <p className="mb-0 text-[0.875rem] leading-[1.25rem] text-[#6C727F]">
                Subject to your compliance with this Agreement, OneCap grants you
                a limited, non-exclusive, non-transferable, non-sublicensable
                license to install and use the Software solely for your personal
                or internal business purposes.
              </p>
            </section>

            <section className="mb-[1.25rem]">
              <h3 className="mb-[0.5rem] text-[1rem] font-bold leading-[1.5rem] text-[#12172B]">
                2. Restrictions
              </h3>
              <p className="mb-[0.625rem] text-[0.875rem] leading-[1.25rem] text-[#6C727F]">
                You shall not:
              </p>
              <ul className="m-0 list-none p-0">
                {[
                  "Copy, modify, or create derivative works of the Software",
                  "Reverse engineer, decompile, or disassemble the Software",
                  "Sell, distribute, sublicense, lease, or rent the Software",
                  "Use the Software for any unlawful or unauthorized purpose",
                  "Attempt to gain unauthorized access to any system, data, or network connected to the Software",
                ].map((item) => (
                  <li
                    key={item}
                    className="relative mb-[0.375rem] pl-[1.125rem] text-[0.875rem] leading-[1.25rem] text-[#6C727F] last:mb-0 before:absolute before:left-[0.125rem] before:top-[0.5rem] before:h-[0.25rem] before:w-[0.25rem] before:rounded-full before:bg-[#99A1AF] before:content-['']"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            <section className="mb-[1.25rem]">
              <h3 className="mb-[0.5rem] text-[1rem] font-bold leading-[1.5rem] text-[#12172B]">
                3. Ownership
              </h3>
              <p className="mb-0 text-[0.875rem] leading-[1.25rem] text-[#6C727F]">
                The Software, including all intellectual property rights therein,
                is and remains the property of the Company. This Agreement does
                not grant you any rights to trademarks, service marks, or logos of
                the Company.
              </p>
            </section>

            <section className="mb-[1.25rem]">
              <h3 className="mb-[0.5rem] text-[1rem] font-bold leading-[1.5rem] text-[#12172B]">
                4. Data Collection and Privacy
              </h3>
              <p className="mb-0 text-[0.875rem] leading-[1.25rem] text-[#6C727F]">
                The Company may collect certain information from you and your
                devices when you use the Software. All information collection is
                subject to our Privacy Policy, which is incorporated by reference
                into this Agreement.
              </p>
            </section>

            <section className="mb-[1.25rem]">
              <h3 className="mb-[0.5rem] text-[1rem] font-bold leading-[1.5rem] text-[#12172B]">
                5. Termination
              </h3>
              <p className="mb-0 text-[0.875rem] leading-[1.25rem] text-[#6C727F]">
                This Agreement is effective until terminated. Your rights under
                this Agreement will terminate automatically without notice if you
                fail to comply with any of its terms. Upon termination, you shall
                cease all use of the Software and destroy all copies.
              </p>
            </section>

            <section className="mb-[1.25rem]">
              <h3 className="mb-[0.5rem] text-[1rem] font-bold leading-[1.5rem] text-[#12172B]">
                6. Disclaimer of Warranties
              </h3>
              <p className="mb-0 text-[0.875rem] leading-[1.25rem] text-[#6C727F]">
                THE SOFTWARE IS PROVIDED &quot;AS IS&quot; WITHOUT WARRANTY OF ANY KIND.
                THE COMPANY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED,
                INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
                NON-INFRINGEMENT.
              </p>
            </section>

            <section className="mb-[1.25rem]">
              <h3 className="mb-[0.5rem] text-[1rem] font-bold leading-[1.5rem] text-[#12172B]">
                7. Limitation of Liability
              </h3>
              <p className="mb-0 text-[0.875rem] leading-[1.25rem] text-[#6C727F]">
                IN NO EVENT SHALL THE COMPANY BE LIABLE FOR ANY SPECIAL,
                INCIDENTAL, INDIRECT, OR CONSEQUENTIAL DAMAGES WHATSOEVER ARISING
                OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO USE THE
                SOFTWARE.
              </p>
            </section>

            <section className="mb-[1.25rem]">
              <h3 className="mb-[0.5rem] text-[1rem] font-bold leading-[1.5rem] text-[#12172B]">
                8. Governing Law
              </h3>
              <p className="mb-0 text-[0.875rem] leading-[1.25rem] text-[#6C727F]">
                This Agreement shall be governed by the laws of India without
                regard to its conflict of law provisions. Any dispute arising
                under this Agreement shall be subject to the exclusive
                jurisdiction of the courts located in Mumbai, India.
              </p>
            </section>

            <section className="mb-[1.25rem]">
              <h3 className="mb-[0.5rem] text-[1rem] font-bold leading-[1.5rem] text-[#12172B]">
                9. Amendments
              </h3>
              <p className="mb-0 text-[0.875rem] leading-[1.25rem] text-[#6C727F]">
                The Company reserves the right to modify this Agreement at any
                time. Such modifications will be effective immediately upon
                posting the modified Agreement on the Company&apos;s website or
                within the Software.
              </p>
            </section>

            <section>
              <h3 className="mb-[0.5rem] text-[1rem] font-bold leading-[1.5rem] text-[#12172B]">
                10. Contact Information
              </h3>
              <p className="mb-0 text-[0.875rem] leading-[1.25rem] text-[#6C727F]">
                If you have any questions about this Agreement, please contact us
                at legal@onecap.com.
              </p>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-[0.75rem] border-t border-[#EDEFF3] px-[1.5rem] pb-[1.5rem] pt-[1rem] max-[480px]:flex-col-reverse max-[480px]:items-stretch">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-[2.25rem] items-center justify-center rounded-[0.375rem] border border-solid border-[#01187D] bg-transparent px-[0.875rem] text-[0.875rem] font-bold leading-none tracking-[-0.15px] text-[#01187D] transition-colors duration-[180ms] hover:bg-[#EBECEC] hover:text-[#12172B] focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_2px_#01187D] active:translate-y-px active:opacity-90 max-[480px]:w-full"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleAccept}
            className="inline-flex h-[2.25rem] items-center justify-center gap-[0.375rem] rounded-[0.375rem] border-none bg-[#01187D] px-[1.25rem] text-[0.875rem] font-bold leading-none tracking-[-0.15px] text-white shadow-[0_2px_4px_0_rgba(0,0,0,0.08),0_4px_6px_0_rgba(0,0,0,0.06)] transition-colors duration-[180ms] hover:bg-[#010F4E] focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_#CCD5FE] active:translate-y-px active:opacity-90 max-[480px]:w-full"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsAndCondition;
